import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, ChevronLeft, ChevronRight, ClipboardList, Gauge, LayoutDashboard, LifeBuoy,
  LogOut, MapPinned, Menu, Search, Settings, Shield, Sparkles, UsersRound, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { AI_TOOLS, PLANS } from '../plans';
import BrandLogo from '../components/BrandLogo';
import { Badge, PlanBadge, UsageMeter } from '../components/ui';
import './appShell.css';

const NAV_GROUPS = [
  { id: 'main', label: null, items: [{ to: '/dashboard', label: 'Overview', icon: LayoutDashboard, end: true }] },
  {
    id: 'growth',
    label: 'Growth',
    items: [
      { to: '/dashboard/visibility', label: 'Visibility', icon: Gauge, badge: 'Free' },
      { to: '/dashboard/rank', label: 'Rank Tracking', icon: MapPinned },
      { to: '/dashboard/leads', label: 'Leads', icon: UsersRound },
    ],
  },
  { id: 'ai', label: 'AI Tools', items: [{ to: '/dashboard/ai-studio', label: 'AI Studio', icon: Sparkles }] },
  { id: 'business', label: 'Business', items: [{ to: '/dashboard/operations', label: 'Operations', icon: ClipboardList }] },
  { id: 'account', label: 'Account', items: [{ to: '/dashboard/setup', label: 'Setup', icon: Settings }] },
];

function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setQuery(''); inputRef.current?.focus(); } }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const entries = useMemo(() => {
    const pages = NAV_GROUPS.flatMap(group => group.items.map(item => ({ id: `page-${item.to}`, label: item.label, group: 'Pages', to: item.to })));
    const tools = AI_TOOLS.map(tool => ({ id: `tool-${tool.id}`, label: tool.name, group: 'AI tools', to: `/dashboard/ai-studio/${tool.id}` }));
    const leads = (workspace.prospects || []).map(lead => ({ id: `lead-${lead.id}`, label: lead.business, group: 'Leads', to: '/dashboard/leads' }));
    const keywords = (workspace.keywords || []).map(item => ({ id: `kw-${item.id}`, label: `${item.keyword} · ${item.location}`, group: 'Keywords', to: '/dashboard/rank' }));
    return [...pages, ...tools, ...leads, ...keywords];
  }, [workspace]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    const source = term ? entries.filter(entry => entry.label.toLowerCase().includes(term)) : entries;
    return source.slice(0, 12);
  }, [entries, query]);

  if (!open) return null;

  const go = to => { onClose(); navigate(to); };

  return (
    <div className="shell-palette-backdrop" onMouseDown={onClose}>
      <div className="shell-palette" role="dialog" aria-modal="true" aria-label="Search Data Tallied" onMouseDown={event => event.stopPropagation()}>
        <div className="shell-palette-input">
          <Search size={16} aria-hidden="true" />
          <input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder="Search Data Tallied..." aria-label="Search Data Tallied" />
          <kbd>Esc</kbd>
        </div>
        <ul className="shell-palette-results">
          {results.map(result => (
            <li key={result.id}>
              <button type="button" onClick={() => go(result.to)}>
                <span>{result.label}</span>
                <small>{result.group}</small>
              </button>
            </li>
          ))}
          {!results.length && <li className="shell-palette-empty">No matches for “{query}”.</li>}
        </ul>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('dt_nav_collapsed') === '1');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => { localStorage.setItem('dt_nav_collapsed', collapsed ? '1' : '0'); }, [collapsed]);
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onKey = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setPaletteOpen(true); }
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const plan = workspace.plan || user?.plan || 'launch';
  const usedGenerations = (workspace.generations || []).filter(item => new Date(item.created_at).getMonth() === new Date().getMonth()).length;
  const groups = user?.role === 'admin'
    ? [...NAV_GROUPS, { id: 'admin', label: 'Administration', items: [{ to: '/dashboard/admin', label: 'Accounts', icon: Shield }] }]
    : NAV_GROUPS;

  const handleLogout = () => { logout(); navigate('/'); };

  const sidebar = (
    <div className="shell-sidebar-inner">
      <div className="shell-brand">
        <Link to="/dashboard" aria-label="Data Tallied dashboard">{collapsed ? <span className="shell-brand-mark">DT</span> : <BrandLogo />}</Link>
        <button type="button" className="shell-collapse" onClick={() => setCollapsed(value => !value)} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'} aria-pressed={collapsed}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav className="shell-nav" aria-label="Dashboard">
        {groups.map(group => (
          <div className="shell-nav-group" key={group.id}>
            {group.label && !collapsed && <p className="shell-nav-label">{group.label}</p>}
            <ul>
              {group.items.map(item => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} className={({ isActive }) => `shell-nav-link${isActive ? ' is-active' : ''}`} title={collapsed ? item.label : undefined}>
                    <item.icon size={17} aria-hidden="true" />
                    {!collapsed && <span>{item.label}</span>}
                    {!collapsed && item.badge && <Badge tone="success">{item.badge}</Badge>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="shell-foot">
        {!collapsed && (
          <div className="shell-plan">
            <div className="shell-plan-head"><span className="dt-label">Current plan</span><PlanBadge plan={plan} /></div>
            <UsageMeter label="AI generations this month" used={usedGenerations} limit={workspace.limits?.aiGenerations || 25} />
            {plan !== 'revenue_partner' && <Link to="/#pricing" className="shell-upgrade">Upgrade plan <ArrowUpRight size={13} /></Link>}
          </div>
        )}
        <a className="shell-foot-link" href="mailto:host@loadedloaded.com?subject=Data%20Tallied%20support" title={collapsed ? 'Help and support' : undefined}>
          <LifeBuoy size={17} aria-hidden="true" />{!collapsed && <span>Help &amp; support</span>}
        </a>
        <div className="shell-account">
          <span className="shell-avatar" aria-hidden="true">{user?.name?.charAt(0).toUpperCase()}</span>
          {!collapsed && <span className="shell-account-copy"><strong>{user?.name}</strong><small>{PLANS[plan]?.name || 'Launch'}{user?.role === 'admin' ? ' · Admin' : ''}</small></span>}
          <button type="button" className="shell-signout" onClick={handleLogout} aria-label="Sign out" title="Sign out"><LogOut size={16} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`shell${collapsed ? ' is-collapsed' : ''}`}>
      <a className="shell-skip" href="#dashboard-main">Skip to main content</a>

      <aside className="shell-sidebar" aria-label="Primary navigation">{sidebar}</aside>

      {drawerOpen && <div className="shell-drawer-backdrop" onClick={() => setDrawerOpen(false)} />}
      <aside className={`shell-drawer${drawerOpen ? ' is-open' : ''}`} aria-label="Primary navigation" aria-hidden={!drawerOpen}>
        <button type="button" className="shell-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><X size={18} /></button>
        {sidebar}
      </aside>

      <div className="shell-body">
        <header className="shell-topbar">
          <button type="button" className="shell-menu" onClick={() => setDrawerOpen(true)} aria-label="Open navigation" aria-expanded={drawerOpen}><Menu size={20} /></button>
          <button type="button" className="shell-search" onClick={() => setPaletteOpen(true)}>
            <Search size={15} aria-hidden="true" />
            <span>Search Data Tallied...</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="shell-topbar-actions">
            <Link to="/" className="shell-topbar-link">View website</Link>
            <PlanBadge plan={plan} />
          </div>
        </header>

        <main className="shell-main" id="dashboard-main" tabIndex={-1}>{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
}
